'use client';

import { Button } from '@savvy-kit/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@savvy-kit/components/popover';
import { cn } from '@savvy-kit/lib/utils';
import { SlidersHorizontal } from 'lucide-react';

import type { ColumnVisibilitySlotProps } from '../../types/column-visibility-slots';

export type DataTableColumnVisibilityProps = Omit<ColumnVisibilitySlotProps, 'showAll' | 'hideAll'>;

export function DataTableColumnVisibility({
  open,
  onOpenChange,
  columns,
  pendingColumnIds,
  disabled,
  toggleColumn,
  apply,
  reset,
  className,
}: DataTableColumnVisibilityProps) {
  const onlyOneSelected = pendingColumnIds.length === 1;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger render={<Button variant="secondary" size="sm" disabled={disabled} className="ml-auto" />}>
        <SlidersHorizontal />
        Display
      </PopoverTrigger>
      <PopoverContent align="end" className={cn('w-80 p-0', className)}>
        <div>
          <div className="space-y-3 p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-dark">Display properties</span>
              <span className="text-sm text-secondary">
                {pendingColumnIds.length}/{columns.length} selected
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {columns.map((column) => {
                const isSelected = pendingColumnIds.includes(column.id);
                const isLastSelected = isSelected && onlyOneSelected;
                return (
                  <button
                    key={column.id}
                    type="button"
                    onClick={() => toggleColumn(column.id)}
                    disabled={isLastSelected}
                    aria-pressed={isSelected}
                    aria-label={
                      isLastSelected ? `${column.header} (at least one column must remain visible)` : column.header
                    }
                    className={cn(
                      'inline-flex h-7 cursor-pointer items-center rounded-full px-3 text-sm text-dark transition-colors',
                      isSelected
                        ? 'border border-dark bg-muted'
                        : 'border border-light bg-white hover:bg-component-hover',
                      isLastSelected && 'cursor-not-allowed opacity-60',
                    )}
                  >
                    {column.header}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="border-t border-light px-3 py-2">
            <div className="flex items-center justify-end gap-1">
              <Button variant="ghost" size="sm" onClick={reset}>
                Reset to default
              </Button>
              <Button variant="ghost" size="sm" onClick={apply}>
                Apply
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
